import * as React from 'react'
import { Link } from 'react-router'
import { Loader2 } from 'lucide-react'
import type { z } from 'zod'
import type { TenantProfileDto } from '@lib/dto/tenant-settings.dto'
import {
  createTenantInputSchema,
  tenantProfileInputSchema,
  type CreateTenantInput,
  type TenantProfileInput,
} from '@lib/input/tenant.input'
import { storedCornerRadiusSchema } from '@lib/enums/corner-radius.enum'
import { storedFontFamilySchema } from '@lib/enums/font-family.enum'
import { suggestTenantSlug } from '@lib/validators/tenant.validator'
import { centsToMicros, microsToWholeCents, parseDollarsToCents } from '@lib/helpers/money'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ColorInput } from '@/components/ui/color-input'
import { Field, fieldMessageId } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { Select } from '@/components/ui/select'
import { ErrorState } from '@/components/PageState'
import { apiErrorMessage, apiFieldErrors } from '@/helpers/apiErrorMessage'
import { CORNER_OPTIONS, FONT_OPTIONS } from './brand-look'
import { timeZoneOptions } from './timeZones'

interface TenantFormValues {
  name: string
  slug: string
  displayName: string
  fromName: string
  replyTo: string
  logoUrl: string
  accentColor: string
  cornerRadius: string
  fontFamily: string
  timezone: string
  /** Dollars as typed ("5.00"); the key matches the schema's, so its messages land on this field. */
  paperCopyFeeMicros: string
  ownerEmail: string
}

type FieldKey = keyof TenantFormValues
type FieldErrors = Partial<Record<FieldKey, string>>

const EMPTY: TenantFormValues = {
  name: '',
  slug: '',
  displayName: '',
  fromName: '',
  replyTo: '',
  logoUrl: '',
  accentColor: '',
  cornerRadius: 'medium',
  fontFamily: 'inter',
  timezone: 'America/Denver',
  paperCopyFeeMicros: '0.00',
  ownerEmail: '',
}

/** While creating, these copy the name until someone edits them. */
const FOLLOWS_NAME: FieldKey[] = ['slug', 'displayName', 'fromName']

function isFieldKey(key: PropertyKey | undefined): key is FieldKey {
  return typeof key === 'string' && key in EMPTY
}

const FIELD_KEYS: FieldKey[] = Object.keys(EMPTY).filter(isFieldKey)

function fieldErrors(error: z.ZodError): FieldErrors {
  const errors: FieldErrors = {}
  for (const issue of error.issues) {
    const key = issue.path[0]
    if (isFieldKey(key) && !errors[key]) errors[key] = issue.message
  }
  return errors
}

function valuesFrom(tenant: TenantProfileDto): TenantFormValues {
  return {
    name: tenant.name,
    slug: tenant.slug,
    displayName: tenant.displayName,
    fromName: tenant.fromName,
    replyTo: tenant.replyTo,
    logoUrl: tenant.logoUrl ?? '',
    accentColor: tenant.accentColor ?? '',
    cornerRadius: tenant.cornerRadius,
    fontFamily: tenant.fontFamily,
    timezone: tenant.timezone,
    paperCopyFeeMicros: (microsToWholeCents(tenant.paperCopyFeeMicros) / 100).toFixed(2),
    ownerEmail: '',
  }
}

/** The paper-copy fee typed in dollars; empty is free. Null when it isn't an amount. */
function paperCopyFeeMicrosFrom(text: string): number | null {
  if (text.trim() === '') return 0
  const cents = parseDollarsToCents(text)
  return cents === null ? null : centsToMicros(cents)
}

type TenantFormProps = {
  /** Where Cancel goes; no Cancel button without it (the dashboard's settings page). */
  cancelTo?: string
} & (
  | { mode: 'create'; onSubmit: (input: CreateTenantInput) => Promise<void> }
  | { mode: 'edit'; initial: TenantProfileDto; onSubmit: (input: TenantProfileInput) => Promise<void> }
)

/**
 * The tenant profile form: staff onboarding and editing a tenant, and owners
 * editing their own organization. The slug is chosen once, at creation;
 * afterwards it shows but can't change.
 * Validates with the shared lib schemas, so the field messages match the API's.
 */
export function TenantForm(props: TenantFormProps) {
  const creating = props.mode === 'create'
  const initial = props.mode === 'edit' ? props.initial : null
  const [values, setValues] = React.useState<TenantFormValues>(() => (initial ? valuesFrom(initial) : EMPTY))
  const [edited, setEdited] = React.useState<ReadonlySet<FieldKey>>(() => new Set())
  const [errors, setErrors] = React.useState<FieldErrors>({})
  const [formError, setFormError] = React.useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const zones = React.useMemo(() => timeZoneOptions(initial?.timezone), [initial?.timezone])

  function set(key: FieldKey, value: string): void {
    setEdited((previous) => new Set(previous).add(key))
    setValues((previous) => {
      const next = { ...previous, [key]: value }
      if (creating && key === 'name') {
        for (const follower of FOLLOWS_NAME) {
          if (edited.has(follower)) continue
          next[follower] = follower === 'slug' ? suggestTenantSlug(value) : value
        }
      }
      return next
    })
  }

  function profile(paperCopyFeeMicros: number) {
    return {
      name: values.name,
      displayName: values.displayName,
      fromName: values.fromName,
      replyTo: values.replyTo,
      logoUrl: values.logoUrl.trim() || null,
      accentColor: values.accentColor.trim() || null,
      cornerRadius: values.cornerRadius,
      fontFamily: values.fontFamily,
      timezone: values.timezone,
      paperCopyFeeMicros,
    }
  }

  /** Validates and submits; returns field errors instead of submitting when invalid. */
  async function submit(): Promise<FieldErrors | null> {
    const feeMicros = paperCopyFeeMicrosFrom(values.paperCopyFeeMicros)
    if (feeMicros === null) return { paperCopyFeeMicros: 'Enter an amount, such as 5.00, or 0 for free' }
    if (props.mode === 'create') {
      const parsed = createTenantInputSchema.safeParse({ ...profile(feeMicros), slug: values.slug, ownerEmail: values.ownerEmail })
      if (!parsed.success) return fieldErrors(parsed.error)
      await props.onSubmit(parsed.data)
      return null
    }
    const parsed = tenantProfileInputSchema.safeParse(profile(feeMicros))
    if (!parsed.success) return fieldErrors(parsed.error)
    await props.onSubmit(parsed.data)
    return null
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setErrors({})
    setFormError(null)
    setIsSubmitting(true)
    try {
      const invalid = await submit()
      if (invalid) setErrors(invalid)
    } catch (failure) {
      const fields = apiFieldErrors(failure, FIELD_KEYS)
      if (fields) setErrors(fields)
      else setFormError(apiErrorMessage(failure, 'Could not save the tenant.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  /** Props tying a control to its Field's message. */
  function control(key: FieldKey) {
    const id = `tenant-${key}`
    return {
      id,
      value: values[key],
      'aria-invalid': errors[key] ? true : undefined,
      'aria-describedby': fieldMessageId(id),
    }
  }

  return (
    <form className="max-w-3xl space-y-6" onSubmit={handleSubmit} noValidate>
      {formError ? <ErrorState message={formError} /> : null}

      <Card>
        <CardHeader>
          <CardTitle>Organization</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" htmlFor="tenant-name" error={errors.name} hint="The organization's name in PixelSign.">
            <Input {...control('name')} autoComplete="off" onChange={(event) => set('name', event.target.value)} />
          </Field>
          <Field
            label="Slug"
            htmlFor="tenant-slug"
            error={errors.slug}
            hint={creating ? 'A short, permanent handle. It cannot be changed later.' : 'Permanent.'}
          >
            <Input
              {...control('slug')}
              className="font-mono"
              autoComplete="off"
              spellCheck={false}
              disabled={!creating}
              onChange={(event) => set('slug', event.target.value)}
            />
          </Field>
          <Field label="Time zone" htmlFor="tenant-timezone" error={errors.timezone} hint="Dates in emails, signed date fields, and the certificate, and when the daily reminder limit resets.">
            <Select {...control('timezone')} onChange={(event) => set('timezone', event.target.value)}>
              <optgroup label="Common">
                {zones.common.map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </optgroup>
              {zones.other.length > 0 ? (
                <optgroup label="All time zones">
                  {zones.other.map((zone) => (
                    <option key={zone} value={zone}>
                      {zone}
                    </option>
                  ))}
                </optgroup>
              ) : null}
            </Select>
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>What signers see</CardTitle>
          <CardDescription>The name, sender, and look on signing pages and emails.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Display name" htmlFor="tenant-displayName" error={errors.displayName} hint="Shown to signers.">
            <Input {...control('displayName')} onChange={(event) => set('displayName', event.target.value)} />
          </Field>
          <Field label="Email sender name" htmlFor="tenant-fromName" error={errors.fromName}>
            <Input {...control('fromName')} onChange={(event) => set('fromName', event.target.value)} />
          </Field>
          <Field label="Reply-to email" htmlFor="tenant-replyTo" error={errors.replyTo} hint="Where signers' replies go, and where the consent tells them to ask for paper or withdraw. Use an address someone reads.">
            <Input
              {...control('replyTo')}
              type="email"
              autoComplete="off"
              onChange={(event) => set('replyTo', event.target.value)}
            />
          </Field>
          <Field
            label="Paper-copy fee"
            htmlFor="tenant-paperCopyFeeMicros"
            error={errors.paperCopyFeeMicros}
            hint="In dollars; 0 is free. Signers can ask for a paper copy, which you mail, and the consent they agree to states this fee. Each request is emailed to the reply-to address."
          >
            <Input
              {...control('paperCopyFeeMicros')}
              inputMode="decimal"
              className="sm:max-w-32"
              onChange={(event) => set('paperCopyFeeMicros', event.target.value)}
            />
          </Field>
          <Field label="Accent color" htmlFor="tenant-accentColor" error={errors.accentColor} hint="Optional. Buttons and links on signing pages.">
            <ColorInput
              {...control('accentColor')}
              placeholder="#rrggbb"
              onChange={(value) => set('accentColor', value)}
            />
          </Field>
          <Field
            label="Corners"
            htmlFor="tenant-cornerRadius"
            error={errors.cornerRadius}
            hint="Buttons, fields, and cards on signing pages and signer email. Medium is PixelSign's."
          >
            <SegmentedControl
              id="tenant-cornerRadius"
              aria-describedby={fieldMessageId('tenant-cornerRadius')}
              ariaLabel="Corners"
              options={CORNER_OPTIONS}
              value={storedCornerRadiusSchema.parse(values.cornerRadius)}
              onChange={(value) => set('cornerRadius', value)}
            />
          </Field>
          <Field
            label="Font"
            htmlFor="tenant-fontFamily"
            error={errors.fontFamily}
            hint="System uses each signer's own device font, closer to an app that does the same."
          >
            <SegmentedControl
              id="tenant-fontFamily"
              aria-describedby={fieldMessageId('tenant-fontFamily')}
              ariaLabel="Font"
              options={FONT_OPTIONS}
              value={storedFontFamilySchema.parse(values.fontFamily)}
              onChange={(value) => set('fontFamily', value)}
            />
          </Field>
          <Field
            label="Logo URL"
            htmlFor="tenant-logoUrl"
            error={errors.logoUrl}
            hint="Optional. An https:// link to a PNG, JPEG, GIF, or WebP image (up to 1 MB). PixelSign keeps its own copy."
            className="sm:col-span-2"
          >
            <Input
              {...control('logoUrl')}
              type="url"
              placeholder="https://"
              onChange={(event) => set('logoUrl', event.target.value)}
            />
          </Field>
        </CardContent>
      </Card>

      {creating ? (
        <Card>
          <CardHeader>
            <CardTitle>Owner</CardTitle>
            <CardDescription>
              We email them an invitation to set a password. They invite the rest of their team.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Field label="Owner email" htmlFor="tenant-ownerEmail" error={errors.ownerEmail} className="sm:max-w-sm">
              <Input
                {...control('ownerEmail')}
                type="email"
                autoComplete="off"
                placeholder="owner@example.com"
                onChange={(event) => set('ownerEmail', event.target.value)}
              />
            </Field>
          </CardContent>
        </Card>
      ) : null}

      <div className="flex justify-end gap-2">
        {props.cancelTo ? (
          <Button variant="secondary" asChild>
            <Link to={props.cancelTo}>Cancel</Link>
          </Button>
        ) : null}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="animate-spin" /> : null}
          {creating ? 'Create and invite owner' : 'Save changes'}
        </Button>
      </div>
    </form>
  )
}
